import type { ElementType } from 'react';
import type { PolymorphicProps } from '#pie/constants/interfaces';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { tv } from '#pie/utils/TailwindUtils';
import { Button } from '../button/Button';
import { Icon, type IconProps } from '../icon/Icon';

export interface IconButtonProps
  extends VariantProps<typeof iconButton>,
    Pick<IconProps, 'icon' | 'size'> {
  className?: string;
}

export function IconButton<TComponent extends ElementType = typeof Button>({
  icon,
  size,
  variant = 'grey',
  isActive,
  as,
  className,
  ...props
}: PolymorphicProps<TComponent, IconButtonProps>) {
  const Component = as ?? Button;
  const style = iconButton({ isActive, variant });

  return (
    <Component className={style.base({ className })} {...props}>
      <Icon icon={icon} size={size} className={style.icon()} />
    </Component>
  );
}

const iconButton = tv({
  base: 'inline-flex group bg-transparent p-1 rounded-full text-content-reversed border hover:bg-inherit focus:outline-action-primary-enabled focus:text-action-primary-focus',
  compoundVariants: [
    {
      class: {
        base: 'bg-surface-minimal-gray text-action-primary-enabled',
        icon: 'stroke-action-primary-enabled',
      },
      isActive: true,
      variant: 'grey',
    },
  ],
  slots: {
    icon: 'stroke-transparent stroke-2',
  },
  variants: {
    isActive: {
      true: {
        base: 'bg-transparent focus:text-inherit',
        icon: 'stroke-transparent',
      },
    },
    variant: {
      grey: {
        base: 'bg-surface-minimal-gray border-action-tertiary-enabled hover:bg-surface-disabled hover:border-action-tertiary-hover focus:bg-surface-subtle-gray focus:border-border-subtle-enabled',
        icon: 'text-content-secondary',
      },
      primary: {
        base: 'bg-surface-base border-action-primary-enabled hover:bg-surface-minimal-gray hover:border-action-primary-hover focus:bg-surface-minimal-gray focus:text-action-primary-focus active:bg-surface-minimal-gray active:text-action-primary-active',
        icon: 'text-action-primary-enabled group-hover:text-action-primary-hover group-focus:text-action-primary-focus group-active:text-action-primary-active',
      },
    },
  },
});
